/**
 * DIPG Mobile Navigation Module
 * Handles DIPG mobile navigation functionality with black and white theme
 */

import { onDOMReady, devLog } from './utils.js';

// Create a factory function that returns the DIPG Mobile Navigation class
// This ensures BaseNavigation is available when the class is created
async function createDIPGMobileNavigation() {
    try {
        const { BaseNavigation } = await import('./base-navigation.js');
        
        class DIPGMobileNavigation extends BaseNavigation {
            constructor() {
                super({ selector: '#dipg-mobile-navigation' });
                this.mobileNav = this.nav;
                this.body = document.body;
                this.init();
            }

            init() {
                if (!this.mobileNav) {
                    this.retryInit();
                    return;
                }
                
                this.createExpandButtons();
                this.bindEvents();
                this.initializeToggles();
            }

            retryInit() {
                let attempts = 0;
                const maxAttempts = 10;
                
                const retry = () => {
                    attempts++;
                    
                    // Try to find the element again
                    this.mobileNav = document.querySelector('#dipg-mobile-navigation');
                    
                    if (this.mobileNav) {
                        this.init(); // Re-run init with the found element
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, 100);
                    }
                };
                
                retry();
            }

            // Override base navigation bindEvents to add mobile-specific events
            bindEvents() {
                super.bindEvents();
                
                // Mobile-specific event binding
                this.bindMobileEvents();
            }

            bindMobileEvents() {
                // DIPG Mobile menu open/close
                const openButtons = document.querySelectorAll('.mobile-menu-toggle');
                const closeButton = this.mobileNav?.querySelector('.dipg-mobile-menu-close');

                // DIPG Mobile navigation event binding
                openButtons.forEach((button, index) => {
                    button.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.openMobileNav();
                    });
                });

                if (closeButton) {
                    closeButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.closeMobileNav();
                    });
                }

                // ESC key to close
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && this.mobileNav?.classList.contains('is-open')) {
                        this.closeMobileNav();
                    }
                });

                // Click outside to close
                if (this.mobileNav) {
                    this.mobileNav.addEventListener('click', (e) => {
                        if (e.target === this.mobileNav) {
                            this.closeMobileNav();
                        }
                    });
                }
            }

            createExpandButtons() {
                if (!this.mobileNav) return;
                
                // Find all menu items with children that need expand buttons
                // Look for items with sub-menu, dipg-mega-menu, dipg-flat-menu, or flyout-menu classes
                // Search within the entire mobile nav container, including nested navigation
                const menuItemsWithChildren = this.mobileNav.querySelectorAll('.menu-item-has-children, .menu-item.dipg-mega-menu, .menu-item.dipg-flat-menu, .menu-item.flyout-menu');
                
                menuItemsWithChildren.forEach((menuItem, index) => {
                    if (menuItem.querySelector('.menu-toggle')) {
                        return; // Skip if toggle already exists
                    }
                    
                    // Look for submenu with the correct class structure for DIPG
                    const hasSubmenu = menuItem.querySelector('.sub-menu.mega-menu, .sub-menu');
                    if (!hasSubmenu) {
                        return;
                    }
                    
                    // Create wrapper if it doesn't exist
                    let wrapper = menuItem.querySelector('.menu-item-wrapper');
                    if (!wrapper) {
                        const link = menuItem.querySelector('a');
                        if (link) {
                            wrapper = document.createElement('div');
                            wrapper.className = 'menu-item-wrapper';
                            link.parentNode.insertBefore(wrapper, link);
                            wrapper.appendChild(link);
                        }
                    }
                    
                    if (wrapper) {
                        // Add has-submenu class
                        menuItem.classList.add('has-submenu');
                        
                        // Create toggle button
                        const toggle = document.createElement('button');
                        toggle.className = 'menu-toggle';
                        toggle.setAttribute('aria-expanded', 'false');
                        toggle.setAttribute('aria-label', 'Toggle submenu');
                        
                        // Create toggle button with CSS background icon (matches main mobile navigation)
                        toggle.innerHTML = '<span class="menu-toggle-icon"></span>';
                        
                        wrapper.appendChild(toggle);
                    }
                });
            }

            initializeToggles() {
                if (!this.mobileNav) return;
                
                const toggles = this.mobileNav.querySelectorAll('.menu-toggle');
                
                toggles.forEach((toggle, index) => {
                    // Remove any existing event listeners by cloning the element
                    const newToggle = toggle.cloneNode(true);
                    toggle.parentNode.replaceChild(newToggle, toggle);
                    
                    newToggle.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.toggleSubmenu(newToggle);
                    });
                });
            }

            toggleSubmenu(toggle) {
                const menuItem = toggle.closest('.menu-item');
                const submenu = menuItem?.querySelector('.sub-menu.mega-menu, .sub-menu');
                
                if (submenu) {
                    super.toggleSubmenu(toggle, submenu);
                }
            }

            openMobileNav() {
                if (!this.mobileNav) {
                    return;
                }
                
                this.mobileNav.classList.add('is-open');
                this.body.classList.add('dipg-mobile-navigation-open');
                
                // Focus management
                const firstFocusable = this.mobileNav.querySelector('button, input, a[href]');
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }

            closeMobileNav() {
                if (!this.mobileNav) return;
                
                this.mobileNav.classList.remove('is-open');
                this.body.classList.remove('dipg-mobile-navigation-open');
                
                // Close all submenus
                const openToggles = this.mobileNav.querySelectorAll('.menu-toggle.is-open');
                openToggles.forEach(toggle => {
                    const submenu = toggle.closest('.menu-item')?.querySelector('.sub-menu.mega-menu, .sub-menu');
                    if (submenu) {
                        this.closeSubmenu(toggle, submenu);
                    }
                });
            }
        }

        return DIPGMobileNavigation;
    } catch (error) {
        console.error('Failed to load BaseNavigation:', error);
        // Return a minimal fallback class
        return class DIPGMobileNavigation {
            constructor() {
                this.mobileNav = document.querySelector('#dipg-mobile-navigation');
                this.body = document.body;
                if (this.mobileNav) {
                    this.init();
                }
            }
            
            init() {
                // Minimal initialization
            }
        };
    }
}

// Export the factory function
export default createDIPGMobileNavigation; 